import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const theme = {
  primaryDark: "#1b325f", primaryBlue: "#282f81", accentCyan: "#11abea",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0"
};

const IpdLabDiagnostics = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [currentMode, setCurrentMode] = useState("add");
  
  // Master Dropdowns
  const [categoriesList, setCategoriesList] = useState([]);
  const [masterTestsList, setMasterTestsList] = useState([]);
  
  // Search States
  const [admissionSearch, setAdmissionSearch] = useState("");
  const [admissionResults, setAdmissionResults] = useState([]);
  const [showAdmissionDropdown, setShowAdmissionDropdown] = useState(false);

  const [doctorSearch, setDoctorSearch] = useState("");
  const [doctorResults, setDoctorResults] = useState([]);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [formData, setFormData] = useState({
    admission_id: "",
    patient_id: "",
    patient_name: "",
    department: "",
    ward_type: "",
    room_no: "",
    bed_no: "",
    age: "",
    gender: "",
    lab_tests_array: []
  });

  const [currentTest, setCurrentTest] = useState({
    order_time: new Date().toISOString().slice(0, 16),
    test_category: "",
    test_id: "",
    test_name: "",
    priority: "Routine",
    reason: "",
    result: "",
    status: "Pending"
  });

  // Derived filtered tests based on selected category
  const filteredTestsForDropdown = currentTest.test_category 
    ? masterTestsList.filter(t => t.category === currentTest.test_category)
    : [];

  // --- MASTER DATA ---
  useEffect(() => {
    const fetchMasters = async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      try {
        const [catRes, testRes] = await Promise.all([
          fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd/lab-categories?employee_id=${user?.id}`),
          fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd/lab-master-tests?employee_id=${user?.id}`)
        ]);
        if (catRes.ok) setCategoriesList(await catRes.json());
        if (testRes.ok) setMasterTestsList(await testRes.json());
      } catch (err) {
        console.error("Error fetching lab master data");
      }
    };
    fetchMasters();
  }, []);

  // --- ADMISSION SEARCH ---
  const handleAdmissionSearch = async (val) => {
    setAdmissionSearch(val);
    if (val.length < 3) {
      setAdmissionResults([]);
      setShowAdmissionDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/admission-vitals/search?q=${val}&employee_id=${user?.id}`);
      const data = await res.json();
      setAdmissionResults(data);
      setShowAdmissionDropdown(true);
    } catch (err) { console.error(err); }
  };

  const loadExistingTests = async (admissionId) => {
    try {
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd-lab-tests/${admissionId}`);
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, lab_tests_array: data }));
      }
    } catch (err) { console.error("Failed to load existing tests"); }
  };

  const selectAdmission = (adm) => {
    setFormData(prev => ({
      ...prev,
      admission_id: adm.admission_id,
      patient_id: adm.patient_id,
      patient_name: adm.patient_name,
      department: adm.department,
      ward_type: adm.ward_type,
      room_no: adm.room_no,
      bed_no: adm.bed_no,
      age: adm.age,
      gender: adm.gender
    }));
    setAdmissionSearch(adm.admission_id);
    setShowAdmissionDropdown(false);
    
    if (screenAction === "edit" || screenAction === "view") {
        loadExistingTests(adm.admission_id);
    }
    
    if (screenAction === "edit" || screenAction === "add") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- ORDERING DOCTOR SEARCH ---
  const handleDoctorSearch = async (val) => {
    setDoctorSearch(val);
    if (val.length < 3) {
      setDoctorResults([]);
      setShowDoctorDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd/doctors/search?q=${val}&employee_id=${user?.id}`);
      const data = await res.json();
      setDoctorResults(data);
      setShowDoctorDropdown(true);
    } catch (err) { console.error(err); }
  };

  const selectDoctor = (doc) => {
    setSelectedDoctor(doc);
    setDoctorSearch(doc.doctor_name);
    setShowDoctorDropdown(false);
  };

  // --- ARRAY MANAGEMENT ---
  const addTest = () => {
    if (!selectedDoctor) return alert("Please select the Recommending Doctor.");
    if (!currentTest.test_id) return alert("Please select a Test Category and Test Name.");
    if (!currentTest.reason) return alert("Please provide a Reason for Test.");

    setFormData(prev => ({
      ...prev,
      lab_tests_array: [{
          ...currentTest,
          ordered_by_cd: selectedDoctor.doc_cd,
          ordered_by_name: selectedDoctor.doctor_name
      }, ...prev.lab_tests_array]
    }));

    setCurrentTest({
      order_time: new Date().toISOString().slice(0, 16),
      test_category: "",
      test_id: "",
      test_name: "",
      priority: "Routine",
      reason: "",
      result: "",
      status: "Pending"
    });
  };

  const removeTest = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      lab_tests_array: prev.lab_tests_array.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  // --- ACTIONS ---
  const clearForm = () => {
    setFormData({ admission_id: "", patient_id: "", patient_name: "", department: "", ward_type: "", room_no: "", bed_no: "", age: "", gender: "", lab_tests_array: [] });
    setAdmissionSearch("");
    setDoctorSearch("");
    setSelectedDoctor(null);
    setIsDisabled(screenAction === "view");
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") { setCurrentMode("add"); clearForm(); setIsDisabled(false); }
    if (screenAction === "edit") { setCurrentMode("edit"); setIsDisabled(false); }
    if (screenAction === "view") { setCurrentMode("view"); setIsDisabled(true); }
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleSubmit = async () => {
    if (!formData.admission_id) return alert("Please select an Admission.");
    if (formData.lab_tests_array.length === 0) return alert("No lab tests to save.");

    const user = JSON.parse(localStorage.getItem("user"));
    const payload = {
      admission_id: formData.admission_id,
      created_by: user.id,
      lab_tests: formData.lab_tests_array
    };

    const method = currentMode === "edit" ? "PUT" : "POST";
    const url = currentMode === "edit"
      ? `https://hmsapi.emedhatechnologies.com/master/api/api/ipd-lab-tests/${formData.admission_id}`
      : `https://hmsapi.emedhatechnologies.com/master/api/api/ipd-lab-tests`;

    try {
      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        alert(currentMode === "edit" ? "Lab Orders Updated Successfully!" : "Lab Tests Ordered Successfully!");
        if (currentMode === "add") clearForm();
      } else {
        alert("Error saving tests.");
      }
    } catch (err) {
      alert("Network error.");
    }
  };

  const printForm = () => {
    if (!formData.admission_id) return alert("Please select an admission to print.");
    const newWindow = window.open("", "", "width=1000,height=800");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Lab Diagnostics - ${formData.admission_id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #1b325f; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #64748b; }
            .section-header { background: #f8fafc; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #282f81; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 20px; }
            .data-item { font-size: 13px; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; color: #555; display: inline-block; width: 140px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 13px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top; }
            th { background-color: #f4f0fa; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
            .status-pending { color: #d35400; font-weight: bold; }
            .status-completed { color: #27ae60; font-weight: bold; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>Pathology & Diagnostics Department</p>
            </div>
            <div style="text-align: right;">
              <h2 style="margin:0; color:#282f81;">IPD LAB REQUISITION</h2>
              <p style="margin:2px 0; font-size:12px;">Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Patient & Admission Details</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Admission ID:</span> ${formData.admission_id}</div>
            <div class="data-item"><span class="label">Patient Name:</span> ${formData.patient_name}</div>
            <div class="data-item"><span class="label">Patient ID:</span> ${formData.patient_id}</div>
            <div class="data-item"><span class="label">Age / Gender:</span> ${formData.age} / ${formData.gender}</div>
            <div class="data-item" style="grid-column: span 2"><span class="label">Location:</span> ${formData.department} | ${formData.ward_type} (Room: ${formData.room_no} / Bed: ${formData.bed_no})</div>
          </div>

          <div class="section-header">Ordered Tests & Results</div>
          <table>
            <thead>
              <tr>
                <th style="width: 15%">Order Time</th>
                <th style="width: 25%">Category & Test</th>
                <th style="width: 20%">Priority & Reason</th>
                <th style="width: 20%">Result</th>
                <th style="width: 10%">Status</th>
                <th style="width: 10%">Ordered By</th>
              </tr>
            </thead>
            <tbody>
              ${formData.lab_tests_array.map(t => `
                <tr>
                  <td>${new Date(t.order_time).toLocaleString()}</td>
                  <td>
                    <span style="color:#282f81; font-weight:bold;">${t.test_category}</span><br/>
                    ${t.test_name} <br><span style="font-size:10px; color:#888;">(${t.test_id})</span>
                  </td>
                  <td>
                    <span style="font-weight:bold; color:${t.priority === 'Immediate' ? '#e74c3c' : t.priority === 'Urgent' ? '#f39c12' : '#333'}">${t.priority}</span><br/>
                    ${t.reason}
                  </td>
                  <td>${t.result || 'Awaiting Result...'}</td>
                  <td class="${t.status === 'Completed' ? 'status-completed' : 'status-pending'}">${t.status}</td>
                  <td>${t.ordered_by_name}</td>
                </tr>
              `).join('')}
              ${formData.lab_tests_array.length === 0 ? '<tr><td colspan="6" style="text-align:center;">No lab tests ordered yet.</td></tr>' : ''}
            </tbody>
          </table>

          <div class="footer">System Generated Report - Emedha HMS</div>
        </body>
      </html>
    `;
    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  return (
    <div className="registration-container fade-in">
      
      {/* TOP SEARCH BOX (Only visible in Edit or View Mode) */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Admission </label>
          <input
            type="text"
            value={admissionSearch}
            placeholder="Search by Admission ID, Patient ID, Name, or Mobile..."
            onChange={(e) => handleAdmissionSearch(e.target.value)}
            autoFocus
            autoComplete="off"
          />
          {showAdmissionDropdown && admissionResults.length > 0 && (
            <div className="patient-dropdown">
              {admissionResults.map((adm) => (
                <div key={adm.admission_id} className="patient-item" onClick={() => selectAdmission(adm)}>
                  <strong>{adm.admission_id}</strong> | {adm.patient_name} | {adm.patient_id} | Ph: {adm.mobile}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        
        {/* 1. PATIENT & ADMISSION DETAILS */}
        <div className="form-section-header"><h3>1. Patient & Admission Details</h3></div>
        <div 
          className="form-grid" 
          style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: "15px", 
            alignItems: "flex-end" 
          }}
        >
          {screenAction === "add" ? (
            <div className="input-box" style={{ position: "relative" }}>
              <label>Search Admission <span style={{color:'red'}}>*</span></label>
              <input 
                type="text" 
                value={admissionSearch} 
                onChange={(e) => handleAdmissionSearch(e.target.value)} 
                placeholder="Start typing to search..." 
                autoComplete="off"
              />
              {showAdmissionDropdown && admissionResults.length > 0 && (
                <div className="patient-dropdown" style={{ zIndex: 100, width: "100%", top: "100%" }}>
                  {admissionResults.map((adm) => (
                    <div key={adm.admission_id} className="patient-item" onClick={() => selectAdmission(adm)}>
                      <strong>{adm.admission_id}</strong> | {adm.patient_name} | {adm.patient_id}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="input-box read-only">
              <label>Admission ID</label>
              <input type="text" value={formData.admission_id} readOnly placeholder="Select from search above" />
            </div>
          )}

          <div className="input-box read-only">
            <label>Patient Name</label>
            <input type="text" value={formData.patient_name} readOnly />
          </div>
          <div className="input-box read-only">
            <label>Age / Gender</label>
            <input type="text" value={formData.age && formData.gender ? `${formData.age} / ${formData.gender}` : ""} readOnly />
          </div>
          <div className="input-box read-only">
            <label>Location</label>
            <input type="text" value={formData.department ? `${formData.department} - ${formData.ward_type} (Bed: ${formData.bed_no})` : ""} readOnly />
          </div>
        </div>

        {/* 2. ORDER DETAILS & TEST SELECTION */}
        <div className="form-section-header" style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>2.Lab Test</h3>
          <button 
            type="button" 
            onClick={addTest} 
            disabled={isDisabled || !formData.admission_id}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: theme.primaryBlue, 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: isDisabled || !formData.admission_id ? 'not-allowed' : 'pointer', 
              fontWeight: 'bold',
              opacity: isDisabled || !formData.admission_id ? 0.6 : 1
            }}>
            + Add Lab Test
          </button>
        </div>

        <div 
          className="form-grid" 
          style={{ 
            backgroundColor: "#f9f9f9", 
            padding: "15px", 
            borderRadius: "8px", 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: "15px",
            alignItems: "flex-start" 
          }}
        >
          {/* Row 1 */}
          <div className="input-box">
            <label>Order Date & Time</label>
            <input 
              type="datetime-local" 
              value={currentTest.order_time} 
              onChange={(e) => setCurrentTest({...currentTest, order_time: e.target.value})} 
              disabled={isDisabled} 
            />
          </div>

          <div className="input-box" style={{ position: "relative" }}>
            <label>Recommended By (Doctor) <span style={{color:'red'}}>*</span></label>
            <input 
              type="text" 
              value={doctorSearch} 
              onChange={(e) => handleDoctorSearch(e.target.value)} 
              placeholder="Search Doctor..." 
              disabled={isDisabled}
              autoComplete="off"
            />
            {showDoctorDropdown && doctorResults.length > 0 && (
              <div className="patient-dropdown" style={{ zIndex: 100, width: "100%", top: "100%" }}>
                {doctorResults.map((doc) => (
                  <div key={doc.doc_cd} className="patient-item" onClick={() => selectDoctor(doc)}>
                    {doc.doc_cd} | {doc.doctor_name} | {doc.department}
                  </div>
                ))}
              </div>
            )}
            {selectedDoctor && (
               <p style={{ margin: "5px 0 0 0", fontSize: "12px", color: theme.primaryBlue, fontWeight: "bold" }}>
                 ✓ Selected: {selectedDoctor.doctor_name}
               </p>
            )}
          </div>

          <div className="input-box">
            <label>Test Priority</label>
            <select 
              value={currentTest.priority} 
              disabled={isDisabled}
              onChange={(e) => setCurrentTest({ ...currentTest, priority: e.target.value })}
            >
              <option value="Routine">Routine</option>
              <option value="Urgent">Urgent</option>
              <option value="Immediate">Immediate</option>
            </select>
          </div>

          <div className="input-box">
            <label>Reason for Test <span style={{color:'red'}}>*</span></label>
            <input 
              type="text" 
              value={currentTest.reason} 
              onChange={(e) => setCurrentTest({...currentTest, reason: e.target.value})} 
              disabled={isDisabled} 
              placeholder="e.g. Pre-op screening" 
            />
          </div>

          {/* Row 2: Cascading Filters */}
          <div className="input-box">
            <label>Test Category <span style={{color:'red'}}>*</span></label>
            <select 
              value={currentTest.test_category} 
              disabled={isDisabled}
              onChange={(e) => setCurrentTest({ 
                ...currentTest, 
                test_category: e.target.value,
                test_id: "", // Reset test when category changes
                test_name: "" 
              })}
            >
              <option value="">Select Category</option>
              {categoriesList.map(cat => (
                <option key={cat.test_category_id} value={cat.test_category_name}>{cat.test_category_name}</option>
              ))}
            </select>
          </div>

          <div className="input-box">
            <label>Lab Test Name <span style={{color:'red'}}>*</span></label>
            <select 
              value={currentTest.test_id} 
              disabled={isDisabled || !currentTest.test_category} // Lock until category is chosen
              onChange={(e) => {
                const t = masterTestsList.find(x => x.test_id.toString() === e.target.value);
                setCurrentTest({ ...currentTest, test_id: e.target.value, test_name: t ? t.test_name : "" });
              }}
            >
              <option value="">Select Test</option>
              {filteredTestsForDropdown.map(t => (
                <option key={t.test_id} value={t.test_id}>{t.test_name}</option>
              ))}
            </select>
          </div>

        </div>

        {/* 3. ORDERED TESTS LIST TABLE */}
        {formData.lab_tests_array.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: theme.primaryDark }}>Ordered Lab Tests ({formData.lab_tests_array.length})</h4>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px', minWidth: '900px' }}>
                <thead style={{ backgroundColor: theme.bgLight, borderBottom: '2px solid #ccc' }}>
                  <tr>
                    <th style={{ padding: '10px', width: '15%' }}>Order Time</th>
                    <th style={{ padding: '10px', width: '20%' }}>Category & Test</th>
                    <th style={{ padding: '10px', width: '15%' }}>Priority & Reason</th>
                    <th style={{ padding: '10px', width: '15%' }}>Ordered By</th>
                    <th style={{ padding: '10px', width: '15%' }}>Result</th>
                    <th style={{ padding: '10px', width: '10%' }}>Status</th>
                    <th style={{ padding: '10px', textAlign: 'center', width: '5%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.lab_tests_array.map((test, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #ddd', backgroundColor: test.id ? '#ffffff' : '#f4fcff' }}>
                      
                      <td style={{ padding: '10px' }}>
                        <input 
                          type="datetime-local" 
                          value={new Date(test.order_time).toISOString().slice(0, 16)} 
                          onChange={(e) => {
                            const newArr = [...formData.lab_tests_array];
                            newArr[index].order_time = e.target.value;
                            setFormData({ ...formData, lab_tests_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent' }}
                        />
                      </td>

                      <td style={{ padding: '10px' }}>
                        <span style={{ color: theme.primaryBlue, fontWeight: 'bold' }}>{test.test_category}</span><br/>
                        {test.test_name} <br/><span style={{ fontSize: '10px', color: '#888' }}>({test.test_id})</span>
                      </td>

                      <td style={{ padding: '10px' }}>
                        <span style={{ fontWeight: 'bold', color: test.priority === 'Immediate' ? '#e74c3c' : test.priority === 'Urgent' ? '#f39c12' : '#333' }}>
                          {test.priority}
                        </span><br/>
                        {test.reason}
                      </td>

                      <td style={{ padding: '10px', fontWeight: 'bold' }}>
                        {test.ordered_by_name}
                      </td>
                      
                      <td style={{ padding: '10px' }}>
                        <input 
                          type="text" 
                          value={test.result || ''} 
                          onChange={(e) => {
                            const newArr = [...formData.lab_tests_array];
                            newArr[index].result = e.target.value;
                            setFormData({ ...formData, lab_tests_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', width: '100%' }}
                          placeholder="Pending..."
                        />
                      </td>

                      <td style={{ padding: '10px' }}>
                        <select 
                          value={test.status} 
                          disabled={isDisabled}
                          onChange={(e) => {
                            const newArr = [...formData.lab_tests_array];
                            newArr[index].status = e.target.value;
                            setFormData({ ...formData, lab_tests_array: newArr });
                          }}
                          style={{ 
                            border: isDisabled ? 'none' : '1px solid #ccc', 
                            background: 'transparent', 
                            color: test.status === 'Completed' ? '#27ae60' : test.status === 'Pending' ? '#d35400' : theme.textMain,
                            fontWeight: 'bold'
                          }}
                        >
                          <option value="Pending">Pending</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </td>
                      
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {!isDisabled && ( 
                          <button 
                            type="button" 
                            onClick={() => removeTest(index)} 
                            style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold', padding: '5px' }}
                            title="Remove Record"
                          >
                            X
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default IpdLabDiagnostics;